





















































































































































import { computed, defineComponent, PropType, ref, nextTick } from '@nuxtjs/composition-api';
import { useTranslations } from '@vue-storefront/novulo';
import useWindowScroll from '~/hooks/useScrollWindow';
import { HEADER_HEIGHT } from '~/helpers/constants';
import { useSortingOptions } from '~/composables/useSortingOptions';
import { PLP_PRODUCTS_PER_PAGE } from '@vue-storefront/novulo-theme/helpers/constants';

export default defineComponent({
  emits: ['sort:update', 'load:nextPage', 'load:previousPage'],
  props: {
    activeFacetFilters: { type: Array as PropType<string[][]>, default: () => [] },
    activeNumericFilters: { type: Array as PropType<string[]>, default: () => [] },
    title: { type: String, required: false, default: '' },
    cmsEssentials: { type: Object as PropType<string>, required: true },
    totalCount: { type: Number, required: true },
    nbPages: { type: Number, required: false, default: 0 },
    currentPage: { type: Number, required: true },
    products: { type: Array, required: true },
    banners: { type: Array, required: false },
    previousPageNumber: { type: Number, required: false, default: 0 },
    isLoading: { type: Boolean, default: false },
    isLoadingPreviousPage: { type: Boolean, default: false },
    previousPageUrl: { type: String, required: false, default: '' },
    nextPageUrl: { type: String, required: false, default: '' },
    selectedSort: { type: Object, required: true },
    hasMoreProducts: { type: Boolean, required: true },
    facets: { type: Object, required: false, default: () => ({}) },
    facetsStats: { type: Object, required: false, default: () => ({}) },
    srpBannerMinHeight: { type: Boolean, required: false, default: false }
  },
  setup(props, { emit }) {
    const { translate } = useTranslations();

    const filtersCount = ref(0);
    const isMobileFiltersOpen = ref(false);
    const { sortingOptions } = useSortingOptions();

    // stickyFilters
    const { scrollY } = useWindowScroll(0);
    const plpIntro = ref<HTMLElement|null>(null);
    const plpDivider = ref<HTMLElement|null>(null);
    const headerHeight = process.client
      ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'))
      : null;

    const headerHeightTotal = process.client
      ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height-total'))
      : null;

    const headerHeightNumber = headerHeightTotal || headerHeight || HEADER_HEIGHT;
    const isSafari = process.client
      ? /safari/i.test(navigator.userAgent) &&
        !/chrome|chromium|android|crios|fxios|edgios|opr/i.test(navigator.userAgent)
      : false;

    const passedStickyFiltersThreshold = computed(() => {
      return (
        Number(plpIntro?.value?.clientHeight) + headerHeightNumber <
        scrollY.value
      );
    });

    // itemIds
    const itemIds = computed(() =>
      props.products?.map((item) => (item as any).sku)
    );

    const handleProductClick = ({ product, index, event }) => {
      emit('product:click', { product, index, event });
    };

    const scrollToIntro = async (behavior: 'smooth' | 'auto' = 'smooth') => {
      await nextTick();

      if (plpIntro.value && headerHeightNumber !== null) {
        const rectTop = plpIntro.value.getBoundingClientRect().top;
        const absoluteTop = rectTop + scrollY.value;
        const scrollPosition = absoluteTop - headerHeightNumber;

        window.scrollTo({
          top: scrollPosition,
          behavior
        });
      }
    };

    const onPaginationClick = async (page: number) => {
      emit('pagination:click', page);

      await scrollToIntro();
    };

    const filtersUpdate = async (e: {
      activeFilters: any[],
      priceRange: { min: number; max: number } | null,
      type: string
    }) => {
      emit('filters:update', e);

      // skip the scroll if this was a price‐change
      if (e.type === 'numeric') return;

      if (isSafari) {
        await scrollToIntro('auto');
        return;
      }

      await scrollToIntro();
    };

    return {
      PLP_PRODUCTS_PER_PAGE,
      filtersCount,
      isMobileFiltersOpen,
      itemIds,
      passedStickyFiltersThreshold,
      plpDivider,
      plpIntro,
      sortingOptions,
      handleProductClick,
      onPaginationClick,
      translate,
      filtersUpdate
    };
  }
});
